import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Edit, Trash2, Mail, Phone, Calendar,
  FileText, BookOpen, AlertCircle, User, MapPin,
  GraduationCap, Hash, Shield, Clock, AlertTriangle, X,
  Printer, RefreshCw, CheckCircle2,
} from 'lucide-react';
import apiClient from '../../config/api.config';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchEtudiantById, fetchEtudiantByMatricule, deleteEtudiant } from '../../store/slices/etudiantSlice';
import { useVerifierEtudiantFraudeQuery } from '../../services/fraudesApi';
import { PageHeader } from '../../components/ui/PageHeader';
import { AlertBanner } from '../../components/ui/AlertBanner';

export const EtudiantDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentEtudiant, loading } = useAppSelector((state) => state.etudiants);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [recalculing, setRecalculing] = useState(false);
  const [recalcMsg, setRecalcMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { data: fraudeData } = useVerifierEtudiantFraudeQuery(
    { etudiant_id: currentEtudiant?.id },
    { skip: !currentEtudiant?.id }
  );

  useEffect(() => {
    if (!id) return;
    // Le paramètre peut être un ID numérique (ex: "142") OU un matricule (ex: "2IP250052").
    // On détecte via regex : si contient une lettre, c'est un matricule.
    if (/^\d+$/.test(id)) {
      dispatch(fetchEtudiantById(parseInt(id, 10)));
    } else {
      dispatch(fetchEtudiantByMatricule(id.toUpperCase()));
    }
  }, [id, dispatch]);

  const handleDelete = async () => {
    if (!currentEtudiant) return;
    setDeleting(true);
    try {
      await dispatch(deleteEtudiant(currentEtudiant.id)).unwrap();
      navigate('/etudiants');
    } catch (err) {
      console.error('Erreur suppression:', err);
      setDeleting(false);
    }
  };

  const handleRachatIntelligent = () => {
    if (!currentEtudiant) return;
    navigate(`/rachat-intelligent?matricule=${currentEtudiant.matricule}`);
  };

  const handleConsulterNotes = () => {
    if (!currentEtudiant) return;
    navigate(`/etudiants/${currentEtudiant.id}/notes`);
  };

  const handleReleveProvisoire = () => {
    if (!currentEtudiant) return;
    navigate(`/releves/notes?matricule=${currentEtudiant.matricule}&type=provisoire`);
  };

  const handleReleveDefinitif = () => {
    if (!currentEtudiant) return;
    navigate(`/releves/notes?matricule=${currentEtudiant.matricule}&type=definitif`);
  };

  const handleRecalculer = async () => {
    if (!currentEtudiant || recalculing) return;
    setRecalculing(true);
    setRecalcMsg(null);
    try {
      const res = await apiClient.post(
        `/portail/admin/recalculer-etudiant/${currentEtudiant.matricule}/`,
        {},
      );
      const d = res.data;
      setRecalcMsg({
        type: 'success',
        text: `Recalcul OK — ${d.ecue_calculees} ECUE, ${d.ue_calculees} UE, ${d.semestres_calcules} semestre(s). La moyenne ne baisse jamais (plancher MAX).`,
      });
      // Refresh la fiche
      if (id) dispatch(fetchEtudiantById(parseInt(id)));
    } catch (err: any) {
      setRecalcMsg({
        type: 'error',
        text: err?.response?.data?.detail || 'Erreur lors du recalcul',
      });
    } finally {
      setRecalculing(false);
    }
  };

  if (loading || !currentEtudiant) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const photoUrl = currentEtudiant.photo_url ||
    (currentEtudiant.photo_path ? `${window.location.hostname === 'localhost' ? 'http://localhost:8000' : ''}/media/${currentEtudiant.photo_path}` : null);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric'
      });
    } catch { return dateStr; }
  };

  return (
    <div className="space-y-6">
      {/* Header (unifié — refonte vague 3 lot 1) */}
      <PageHeader
        title={`${currentEtudiant.nom} ${currentEtudiant.prenoms}`}
        description={<>Fiche étudiant · <span className="font-mono">{currentEtudiant.matricule}</span></>}
        back={
          <Button variant="outline" size="sm" icon={<ArrowLeft size={14} />}
            onClick={() => navigate('/etudiants')}>
            Retour
          </Button>
        }
        actions={
          <>
            <Button
              variant="primary" size="sm" icon={<FileText size={14} />}
              onClick={() => navigate(`/etudiants/${currentEtudiant.matricule}/bilan`)}
              title="Vue 360° : moyennes, présences, conduite, santé, communication..."
            >
              Bilan complet
            </Button>
            <Button
              variant="outline" size="sm"
              icon={<RefreshCw size={14} className={recalculing ? 'animate-spin' : ''} />}
              onClick={handleRecalculer} disabled={recalculing}
              title="Recalcule ECUE, UE et moyenne semestre. Le plancher MAX empêche toute baisse."
            >
              {recalculing ? 'Calcul...' : 'Recalculer'}
            </Button>
            <Button variant="outline" size="sm" icon={<Edit size={14} />}
              onClick={() => navigate(`/etudiants/${currentEtudiant.id}/edit`)}>
              Modifier
            </Button>
            <Button variant="danger" size="sm" icon={<Trash2 size={14} />}
              onClick={() => setShowDeleteModal(true)}>
              Supprimer
            </Button>
          </>
        }
      />

      {/* Feedback recalcul (unifié) */}
      {recalcMsg && (
        <AlertBanner
          type={recalcMsg.type === 'success' ? 'success' : 'error'}
          message={recalcMsg.text}
          onClose={() => setRecalcMsg(null)}
        />
      )}
      {/* Fraud Alert Banner */}
      {fraudeData?.has_fraude && (
        <div className="bg-red-50 dark:bg-red-900/30 border-2 border-red-400 dark:border-red-600 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Shield className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" size={22} />
            <div className="flex-1">
              <h4 className="font-bold text-red-800 dark:text-red-300 text-base">
                ATTENTION — Cet etudiant a {fraudeData.count} cas de fraude actif{fraudeData.count > 1 ? 's' : ''}
              </h4>
              <ul className="mt-2 space-y-1">
                {fraudeData.cas_actifs.map((cas) => (
                  <li key={cas.id} className="text-sm text-red-700 dark:text-red-300 flex items-center gap-2">
                    <AlertTriangle size={14} className="flex-shrink-0" />
                    <span>
                      <strong>{cas.reference}</strong> — {cas.type_fraude_display} — {cas.matiere_nom} — <em>{cas.statut_display}</em>
                    </span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/fraudes')}
                className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline font-medium"
              >
                Voir tous les cas de fraude &rarr;
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile + Info */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

        {/* Photo Card */}
        <Card className="flex flex-col items-center py-8">
          <div className="w-36 h-36 rounded-full overflow-hidden bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center shadow-lg mb-4">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={`${currentEtudiant.nom} ${currentEtudiant.prenoms}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <span className="text-4xl font-bold text-blue-600">
                {currentEtudiant.nom.charAt(0)}{(currentEtudiant.prenoms || '').charAt(0)}
              </span>
            )}
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 text-center">
            {currentEtudiant.nom} {currentEtudiant.prenoms}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 font-mono mt-1">{currentEtudiant.matricule}</p>
          <div className="flex gap-2 mt-3">
            <Badge variant={currentEtudiant.actif ? 'success' : 'danger'}>
              {currentEtudiant.actif ? 'Actif' : 'Inactif'}
            </Badge>
            <Badge variant="info">{currentEtudiant.niveau}</Badge>
          </div>
          {currentEtudiant.classe && (
            <p className="text-xs text-gray-400 mt-2">Classe: {currentEtudiant.classe}</p>
          )}
        </Card>

        {/* Info Card */}
        <Card className="lg:col-span-3">
          <div className="border-b border-gray-200 dark:border-gray-700 pb-3 mb-5">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Informations de l'étudiant</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* Identité */}
            <InfoItem icon={<User size={16} />} label="Nom" value={currentEtudiant.nom} />
            <InfoItem icon={<User size={16} />} label="Prénoms" value={currentEtudiant.prenoms || '-'} />
            <InfoItem icon={<Hash size={16} />} label="Matricule" value={currentEtudiant.matricule} />
            <InfoItem icon={<Shield size={16} />} label="Sexe"
              value={currentEtudiant.sexe === 'M' ? 'Masculin' : currentEtudiant.sexe === 'F' ? 'Féminin' : '-'} />
            <InfoItem icon={<Calendar size={16} />} label="Date de naissance"
              value={formatDate(currentEtudiant.date_naissance)} />
            <InfoItem icon={<MapPin size={16} />} label="Lieu de naissance"
              value={currentEtudiant.lieu_naissance || '-'} />

            {/* Académique */}
            <InfoItem icon={<GraduationCap size={16} />} label="Niveau"
              value={<Badge variant="info">{currentEtudiant.niveau}</Badge>} />
            <InfoItem icon={<BookOpen size={16} />} label="Parcours / Filière"
              value={currentEtudiant.parcours || '-'} />
            <InfoItem icon={<BookOpen size={16} />} label="Option"
              value={currentEtudiant.option || '-'} />
            <InfoItem icon={<FileText size={16} />} label="Pôle"
              value={currentEtudiant.pole || '-'} />
            <InfoItem icon={<Hash size={16} />} label="Classe"
              value={currentEtudiant.classe || '-'} />

            {/* Contact */}
            <InfoItem icon={<Mail size={16} />} label="Email"
              value={currentEtudiant.email || '-'} />
            <InfoItem icon={<Phone size={16} />} label="Téléphone"
              value={currentEtudiant.telephone || '-'} />

            {/* Métadonnées */}
            <InfoItem icon={<Clock size={16} />} label="Date d'import"
              value={formatDate(currentEtudiant.date_import)} />
          </div>
        </Card>
      </div>

      {/* Actions rapides */}
      <Card>
        <div className="border-b border-gray-200 dark:border-gray-700 pb-3 mb-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Actions rapides</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button onClick={handleConsulterNotes}
            className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <BookOpen size={20} className="text-green-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">Consulter les Notes</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Voir et modifier les notes de l'étudiant</p>
            </div>
          </button>
          <button onClick={handleRachatIntelligent}
            className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <AlertCircle size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">Rachat Intelligent</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Proposer un rachat automatique</p>
            </div>
          </button>
          <button onClick={handleReleveProvisoire}
            className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors text-left">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <Printer size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">Relevé Provisoire</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Imprimer le relevé de notes provisoire</p>
            </div>
          </button>
          <button onClick={handleReleveDefinitif}
            className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <FileText size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">Relevé Définitif</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Imprimer le relevé de notes définitif</p>
            </div>
          </button>
        </div>
      </Card>

      {/* Info box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" size={18} />
        <div>
          <h4 className="font-semibold text-blue-900 dark:text-blue-300 text-sm">Informations</h4>
          <p className="text-sm text-blue-800 dark:text-blue-300 mt-1">
            Les relevés de notes et résultats académiques sont accessibles depuis les modules correspondants.
            La classe est générée automatiquement à partir du niveau et de l'option.
          </p>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                <AlertTriangle size={24} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Confirmer la suppression</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              Supprimer l'étudiant <strong>{currentEtudiant.nom} {currentEtudiant.prenoms}</strong> ?
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Matricule: {currentEtudiant.matricule} &mdash; Cette action est irréversible.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowDeleteModal(false)}>
                Annuler
              </Button>
              <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleting}
                icon={deleting ? <Spinner size="sm" /> : <Trash2 size={14} />}>
                Supprimer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Sous-composant pour afficher une info avec icône
const InfoItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}> = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 text-gray-400">{icon}</div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</p>
      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">{value}</div>
    </div>
  </div>
);
